import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { saveAs } from "file-saver";
import Box from "@mui/material/Box";
import Button from "components/button";
import Checkbox from "components/checkbox";
import Divider from "@mui/material/Divider";
import { Grid, Typography } from "@mui/material";
import FileGridItem from "components/files-grid-list/file-grid-item";
import { useTheme } from "styled-components";
import FilesList from "components/files-list";
import PropertyTypeSection from "./property-type-section";
import MapImagesList from "components/files-list/map-images-list";
import FloorRangeInstructionsSection from "./floor-range-instructions-section";
import { useParams } from "react-router-dom";
import FormAlert from "components/form-alert";
import { FormApi } from "apis";
import CircularProgress from "@mui/material/CircularProgress";
import { Container, StyledFooter, StyledSpinnerContainer } from "../style";
import ConfirmChangePropertyTypeModal from "./confirm-change-property-modal";
import MixConstants from "constants/mix-constants";
import FileUploadContainer from "components/file-upload-container";
import Utils from "helpers/utils";

function PropertyMapImagesForm({ expanded, checkStatus, status, closeForm }) {
  const theme = useTheme();

  const [state, setState] = useState({
    selectedPropertyType: null,
    files: [],
    uploadLabeledImages: false,
  });
  // Set from the community's Connect settings: whether this property uses SVG
  // maps at all, and whether it is Beans generated (which adds one shared
  // background map underneath every floor).
  const [svgSettings, setSvgSettings] = useState({
    enable_svg_mode: false,
    is_beans_svg: false,
    background_svg_image: null,
  });
  const [backgroundSvgFile, setBackgroundSvgFile] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const { communityId } = useParams();

  const [submitting, setSubmitting] = useState(false);
  const [finishLater, setFinishLater] = useState(false);
  const propertyTypes = [
    "Garden Style Community",
    "Mid-rise or High-rise Building(s)",
  ];
  const [openPorpertyMapChangeModal, setOpenPorpertyMapChangeModal] =
    useState(false);

  const getPropertyType = useCallback((type) => {
    if (type === "Garden Style Community") {
      return "garden_style_community";
    }
    if (type === "garden_style_community") {
      return "Garden Style Community";
    }
    if (type === "Mid-rise or High-rise Building(s)") {
      return "mid_high_rise_community";
    }
    if (type === "mid_high_rise_community") {
      return "Mid-rise or High-rise Building(s)";
    }
  }, []);

  // Connect keeps a property map in one of two columns -- `svg_image` for an
  // SVG map, `image` (or `file`) for a raster one -- and a record can carry
  // both, left over from whichever it used before. `enable_svg_mode` is what
  // decides which one this property actually uses, so the form reads and
  // writes that column and leaves the other alone. With the toggle off nothing
  // here behaves differently to before.
  const activeMapAsset = useCallback(
    (record, svgMode) =>
      svgMode
        ? record?.svg_image?.url && record?.svg_image
        : (record?.image?.url && record?.image) ||
          (record?.file?.url && record?.file),
    [],
  );

  const getMappedImages = useCallback(
    (propertyMaps, svgMode) => {
      let propertyMap = [];
      let labelImages = false;

      const garden = propertyMaps?.garden_style_community;
      if (garden) {
        if (garden?.label_image?.url !== null) {
          labelImages = true;
        }
        const asset = activeMapAsset(garden, svgMode);
        if (asset) {
          propertyMap.push({
            ...asset,
            id: garden.id,
            photo: asset.url,
            isLocal: false,
            file_type: "image",
            type: "Garden Style Community",
            additionalImage:
              garden?.label_image?.url !== null ? garden.label_image : null,
          });
        }
      }

      if (propertyMaps?.mid_high_rise_community?.length > 0) {
        propertyMaps.mid_high_rise_community.forEach((maps) => {
          if (maps.label_image?.url !== null) {
            labelImages = true;
          }
          const asset = activeMapAsset(maps, svgMode);
          propertyMap.push({
            ...maps,
            id: maps.id,
            photo: asset ? asset.url : null,
            isLocal: false,
            range: maps.range,
            name: maps.name,
            file_type: "image",
            type: "Mid-rise or High-rise Building(s)",
            additionalImage:
              maps?.label_image?.url !== null ? maps.label_image : null,
          });
        });
      }

      return {
        files: propertyMap,
        uploadLabeledImages: labelImages,
      };
    },
    [activeMapAsset],
  );

  // The one place that decides which column an upload is written to.
  const mapFilePayload = useCallback((image, svgMode) => {
    if (!image?.isLocal) {
      return { image: null, file: null, svg_image: null };
    }
    if (svgMode) {
      return { image: null, file: null, svg_image: image?.originalFile };
    }
    return {
      image: Utils.isImageVideo(image) ? image?.originalFile : null,
      file: Utils.isImageVideo(image) ? null : image?.originalFile,
      svg_image: null,
    };
  }, []);

  const svgMode = svgSettings?.enable_svg_mode === true;
  const beansSvg = svgSettings?.is_beans_svg === true;

  // add_property_images reports validation problems (a malformed SVG, a map
  // that is too small) as a list under `data`; show them rather than a generic
  // failure, since they tell the client what to fix.
  const serverError = (response) => {
    if (response?.success !== false) return null;
    return Array.isArray(response?.data) ? response.data.join(" ") : null;
  };

  const formAlertCloseButtonClicked = () => {
    setMessage("");
  };

  const setMessageText = (text, type) => {
    formAlertCloseButtonClicked();
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      formAlertCloseButtonClicked();
    }, 5000);
  };

  const fetchingPropertyMap = useCallback(
    (abortController) => {
      if (expanded && communityId) {
        setFetching(true);
        FormApi.fetchPropertyMap(communityId).then(
          (response) => {
            const aborted = abortController.signal.aborted;
            if (aborted === false) {
              setFetching(false);
              // Settings come back whether or not the property has maps yet,
              // so they are read before the success check.
              const settings = response?.svg_settings ?? {};
              setSvgSettings(settings);
              setBackgroundSvgFile(null);

              if (response?.success) {
                let fetchedPropertyMap = getMappedImages(
                  response.data,
                  settings?.enable_svg_mode,
                );
                setState({
                  selectedPropertyType: getPropertyType(
                    Object.keys(response.data)[0],
                  ),
                  files: fetchedPropertyMap.files,
                  uploadLabeledImages: fetchedPropertyMap.uploadLabeledImages,
                });
              }
            }
          },
          (error) => {
            setFetching(false);
          },
        );
      }
    },
    [expanded, communityId, getMappedImages, getPropertyType],
  );

  const handleDroppedFiles = (files) => {
    if (state.selectedPropertyType === propertyTypes[0]) {
      const filesWithLabeledImage = files.map((file) => {
        return {
          ...file,
          additionalImage: null,
        };
      });
      setState({
        ...state,
        files: _.union(state.files[0], filesWithLabeledImage),
      });
    } else {
      const filesWithLabeledImage = files.map((file) => {
        return {
          ...file,
          additionalImage: null,
        };
      });
      setState({
        ...state,
        files: _.union(state.files, filesWithLabeledImage),
      });
    }
  };

  // FileGridItem draws a thumbnail from `photo`/`url` and falls back to
  // createObjectURL on the file itself -- which throws on the wrapper object
  // FileUploadContainer hands back. Give it a preview URL up front, and revoke
  // it again so the blob is not held for the life of the page.
  const handleBackgroundSvgDropped = (files) => {
    const dropped = files?.[0];
    if (!dropped?.originalFile) return;

    if (backgroundSvgFile?.photo) {
      URL.revokeObjectURL(backgroundSvgFile.photo);
    }
    setBackgroundSvgFile({
      ...dropped,
      photo: URL.createObjectURL(dropped.originalFile),
    });
  };

  const clearBackgroundSvg = () => {
    if (backgroundSvgFile?.photo) {
      URL.revokeObjectURL(backgroundSvgFile.photo);
    }
    setBackgroundSvgFile(null);
  };

  const handleFilesListUpdate = (updatedFilesList) => {
    setState({
      ...state,
      files: updatedFilesList,
    });
  };

  const handleProperyMapDeleted = (deletedPropertymap) => {
    const deleteMessage = () => {
      const index = _.findIndex(state.files, deletedPropertymap);
      let propertyMaps = [...state.files];
      propertyMaps.splice(index, 1);
      setState({
        ...state,
        files: propertyMaps,
      });
    };
    let successMessage = "Property map deleted successfully";
    let errorMessage = "Failed to delete property map";
    setSubmitting(true);
    let type = getPropertyType(deletedPropertymap.type);
    FormApi.deletePropertyMap(communityId, deletedPropertymap.id, type).then(
      (response) => {
        if (response?.success) {
          setMessageText(successMessage, "success");
          setSubmitting(false);
          checkStatus();
          deleteMessage();
        }
      },
      (error) => {
        setMessageText(errorMessage, "error");

        setSubmitting(false);
      },
    );
    setSubmitting(false);
  };

  const handleLabelImageDeleted = (deletedPropertymap, index) => {
    const deleteMessage = () => {
      let filesList = [...state.files];
      filesList[index].additionalImage = null;
      setState({
        ...state,
        files: filesList,
      });
    };
    let successMessage = "Label image deleted successfully";
    let errorMessage = "Failed to delete label image";
    setSubmitting(true);
    let type = getPropertyType(deletedPropertymap.type);
    FormApi.deleteLabelImage(communityId, deletedPropertymap.id, type).then(
      (response) => {
        if (response?.success) {
          setMessageText(successMessage, "success");
          setSubmitting(false);
          checkStatus();
          deleteMessage();
        }
      },
      (error) => {
        setMessageText(errorMessage, "error");

        setSubmitting(false);
      },
    );
    setSubmitting(false);
  };

  const canUserProceed = () => {
    let hasError = false;
    if (state.selectedPropertyType === null) {
      setMessageText("Please select property type before submitting.", "error");
      hasError = true;
      return false;
    }
    if (state?.files?.length === 0) {
      setMessageText(
        `Please add atleast one image for ${state.selectedPropertyType}`,
        "error",
      );
      hasError = true;
      return false;
    }
    if (state.selectedPropertyType === propertyTypes[0]) {
      if (state?.files?.length > 1) {
        setMessageText(
          "Select only one image for garden style property",
          "error",
        );
        hasError = true;
        return false;
      }
      if (state?.files?.length < 1) {
        setMessageText("Select one image for garden style property", "error");
        hasError = true;
        return false;
      }
    }
    if (state.selectedPropertyType === propertyTypes[1]) {
      let floorName = [];
      let floorRange = [];
      state.files.map((file) => {
        if (!file.name) {
          setMessageText("Floor name can not be empty ", "error");
          hasError = true;
          return false;
        }
        if (!file.range) {
          setMessageText("Floor range can not be empty ", "error");
          hasError = true;
          return false;
        }
        if (floorName.includes(file.name)) {
          setMessageText("Floor name must be unique. ", "error");
          hasError = true;
          return false;
        } else {
          floorName.push(file.name);
        }
        // eslint-disable-next-line no-useless-escape
        if (!file?.range?.match(/^[0-9-,\[\]]*$/g)) {
          setMessageText("Floor range should be a number ", "error");
          hasError = true;
          return false;
        } else {
          if (file.range.includes("-")) {
            const values = file.range.split("-");
            for (let i = parseInt(values[0]); i <= parseInt(values[1]); i++) {
              if (floorRange.includes(i)) {
                setMessageText("Floor range must be unique ", "error");
                hasError = true;
                return false;
              } else {
                floorRange.push(i.toString());
              }
            }
          } else {
            if (floorRange.includes(file.range)) {
              setMessageText("Floor range must be unique ", "error");
              hasError = true;
              return false;
            } else {
              floorRange.push(file.range);
            }
          }
        }

        return [];
      });
    }

    return !hasError;
  };

  const onSubmitButtonClicked = () => {
    if (canUserProceed()) {
      let sentData;
      let data;

      if (state.selectedPropertyType === propertyTypes[0]) {
        data = state.files.map((image) => {
          return {
            id: !image.isLocal ? image.id : null,
            ...mapFilePayload(image, svgMode),
            label_image: state.uploadLabeledImages
              ? image.additionalImage.originalFile
              : null,
          };
        });
        sentData = data[0];
      }

      if (state.selectedPropertyType === propertyTypes[1]) {
        data = state.files.map((image) => {
          return {
            id: !image.isLocal ? image.id : null,
            ...mapFilePayload(image, svgMode),
            range: image.range,
            name: image.name,
            label_image:
              image.additionalImage !== null
                ? image.additionalImage.originalFile
                : null,
          };
        });
        sentData = data;
      }

      if (communityId) {
        const successMessage = "Property map submitted successfully";
        const errorMessage = "Failed to submit property map";
        setSubmitting(true);
        FormApi.savePropertyMap(
          communityId,
          sentData,
          getPropertyType(state.selectedPropertyType),
          "",
          backgroundSvgFile?.originalFile,
        ).then(
          (response) => {
            setSubmitting(false);
            if (response?.success && response?.data) {
              let data = getMappedImages(response.data, svgMode);
              setState({
                selectedPropertyType: state.selectedPropertyType,
                files: data.files,
                uploadLabeledImages: data.uploadLabeledImages,
              });
              setSvgSettings(response?.svg_settings ?? svgSettings);
              clearBackgroundSvg();
              checkStatus();
              setMessageText(successMessage, "success");
            } else {
              setMessageText(serverError(response) ?? errorMessage, "error");
            }
          },
          (error) => {
            setSubmitting(false);
            setMessageText(errorMessage, "error");
          },
        );
      }
    }
  };

  const onSaveAndSubmitButtonClicked = () => {
    if (state.files.length > 0) {
      if (canUserProceed()) {
        let sentData;
        let data;

        if (state.selectedPropertyType === propertyTypes[0]) {
          data = state?.files?.map((image) => {
            return {
              id: !image.isLocal ? image.id : null,
              ...mapFilePayload(image, svgMode),
              label_image: state.uploadLabeledImages
                ? image.additionalImage.originalFile
                : null,
            };
          });
          sentData = data[0];
        }

        if (state.selectedPropertyType === propertyTypes[1]) {
          data = state?.files?.map((image) => {
            return {
              id: !image.isLocal ? image.id : null,
              ...mapFilePayload(image, svgMode),
              range: image.range,
              name: image.name,
              label_image:
                image.additionalImage !== null
                  ? image.additionalImage.originalFile
                  : null,
            };
          });
          sentData = data;
        }
        if (communityId) {
          setFinishLater(true);
          FormApi.savePropertyMap(
            communityId,
            sentData,
            getPropertyType(state.selectedPropertyType),
            (status = "in_progress"),
            backgroundSvgFile?.originalFile,
          ).then(
            (response) => {
              setFinishLater(false);

              if (response?.success && response?.data) {
                let data = getMappedImages(response.data, svgMode);
                setState({
                  selectedPropertyType: state.selectedPropertyType,
                  files: data.files,
                  uploadLabeledImages: data.uploadLabeledImages,
                });
                setSvgSettings(response?.svg_settings ?? svgSettings);
                clearBackgroundSvg();
                checkStatus();
              } else {
              }
            },
            (error) => {
              setFinishLater(false);
            },
          );
        }
        closeForm();
      }
    } else {
      formAlertCloseButtonClicked();
      closeForm();
    }
  };

  const handlePropertyTypeChange = (e) => {
    if (state.files.length > 0) {
      state.files.map((file) => {
        if (!file.isLocal) {
          setOpenPorpertyMapChangeModal(true);
        } else {
          setState({
            ...state,
            selectedPropertyType: e.target.value,
          });
        }
        return [];
      });
    } else {
      setState({
        ...state,
        selectedPropertyType: e.target.value,
      });
    }
  };

  const handlePropertyTypeChangeModal = (type) => {
    checkStatus();
    const typeChange =
      getPropertyType(type) === propertyTypes[0]
        ? propertyTypes[1]
        : propertyTypes[0];
    setState({
      ...state,
      files: [],
      selectedPropertyType: typeChange,
    });
    setOpenPorpertyMapChangeModal(false);
  };

  const handleUploadLabeledImagesCheckboxChange = (name, value) => {
    setState({
      ...state,
      uploadLabeledImages: value,
    });
  };

  useEffect(() => {
    let abortController = new AbortController();
    fetchingPropertyMap(abortController);
    return () => {
      abortController.abort();
    };
  }, [fetchingPropertyMap]);

  if (fetching) {
    return (
      <StyledSpinnerContainer>
        <CircularProgress size={20} />
      </StyledSpinnerContainer>
    );
  }

  return (
    <Container>
      <PropertyTypeSection
        types={propertyTypes}
        selectedType={state.selectedPropertyType}
        onChange={handlePropertyTypeChange}
      />
      {state.selectedPropertyType && (
        <Box sx={{ marginTop: "0.5rem" }}>
          <Divider sx={{ marginBottom: "0.5rem" }} />
          <Checkbox
            label='If the image to be displayed in the app does not have a label for every unit number and amenity then upload a separate image with labels for reference for the Pynwheel production team.'
            checked={state.uploadLabeledImages}
            onChange={(e) =>
              handleUploadLabeledImagesCheckboxChange(
                e.target.name,
                e.target.checked,
              )
            }
          />
        </Box>
      )}

      {state.selectedPropertyType && (
        <Box sx={{ marginTop: "0.5rem", marginBottom: "1rem" }}>
          <FileUploadContainer
            accept={svgMode ? ".svg" : ".png, .jpg, .jpeg"}
            onFileDrop={handleDroppedFiles}
            helperText={svgMode ? ".svg" : ".png or .jpg atleast 1400px"}
            validationEnabled={false}
            maxFiles={state.selectedPropertyType === propertyTypes[0] ? 1 : 0}
          />
        </Box>
      )}

      {state.selectedPropertyType && beansSvg && (
        <Box sx={{ marginBottom: "1rem" }}>
          <Divider sx={{ marginBottom: "0.5rem" }} />
          <Typography sx={{ marginBottom: "0.5rem" }}>
            Background map{" "}
            <Typography display='inline' sx={{ fontStyle: "italic" }}>
              (the shared base layer every floor is drawn on top of)
            </Typography>
          </Typography>
          {backgroundSvgFile || svgSettings?.background_svg_image ? (
            <Box sx={{ marginBottom: "0.5rem" }}>
              <FileGridItem
                file={
                  backgroundSvgFile ?? {
                    photo: svgSettings.background_svg_image,
                    type: "image/svg+xml",
                    isLocal: false,
                  }
                }
                onClickDownload={() =>
                  saveAs(
                    backgroundSvgFile
                      ? backgroundSvgFile.originalFile
                      : svgSettings.background_svg_image,
                    "background-map.svg",
                  )
                }
                onClickRemove={clearBackgroundSvg}
                disabled={!backgroundSvgFile}
              />
            </Box>
          ) : null}
          <FileUploadContainer
            accept='.svg'
            onFileDrop={handleBackgroundSvgDropped}
            helperText='.svg'
            validationEnabled={false}
            maxFiles={1}
          />
        </Box>
      )}

      {state.files.length > 0 &&
      state.selectedPropertyType === propertyTypes[1] ? (
        <Box>
          <FloorRangeInstructionsSection />
          <Divider sx={{ marginBottom: "1rem" }} />
        </Box>
      ) : null}

      {state.files.length > 0 ? (
        state.selectedPropertyType === propertyTypes[0] ? (
          <Grid>
            <FilesList
              files={state.files}
              onUpdate={(updatedFilesList) =>
                handleFilesListUpdate(updatedFilesList)
              }
              onDeleteImage={handleProperyMapDeleted}
              withAdditionalImage={state.uploadLabeledImages}
              onDeleteLabelImage={handleLabelImageDeleted}
            />
          </Grid>
        ) : (
          <MapImagesList
            files={state.files}
            onUpdate={(updatedFilesList) =>
              handleFilesListUpdate(updatedFilesList)
            }
            withAdditionalImage={state.uploadLabeledImages}
            onDelete={handleProperyMapDeleted}
            onDeleteLabelImage={handleLabelImageDeleted}
          />
        )
      ) : null}

      <Divider sx={{ marginTop: "0.5rem" }} />
      {message && messageType && (
        <Box>
          <Divider sx={{ marginTop: "1rem", marginBottom: "1rem" }} />
          <FormAlert
            message={message}
            type={messageType}
            onClose={() => formAlertCloseButtonClicked()}
          />
        </Box>
      )}
      {openPorpertyMapChangeModal && (
        <ConfirmChangePropertyTypeModal
          propertyType={getPropertyType(state.selectedPropertyType)}
          onAccept={handlePropertyTypeChangeModal}
          onClose={() => setOpenPorpertyMapChangeModal(false)}
        />
      )}

      <StyledFooter>
        <Button
          bgColor={theme.colors.gray}
          titleColor={theme.colors.white}
          disabled={finishLater}
          loading={finishLater}
          title='Save & Finish Later'
          onClick={onSaveAndSubmitButtonClicked}
        />
        <Button
          bgColor={theme.colors.green}
          title='Submit'
          submitting={submitting}
          disabled={
            status !== MixConstants.FormStatus.DEPLOYED ? submitting : true
          }
          loading={submitting}
          onClick={onSubmitButtonClicked}
        />
      </StyledFooter>
    </Container>
  );
}

export default PropertyMapImagesForm;
